@if ((leadSourceTagsLoadingState$ | async) === 'loading') {
  <div class="flex items-center justify-center p-2 h-32">
    <app-loader [loadingState]="'loading'" [inline]="true" class="h-32 w-full"></app-loader>
  </div>
} @else {
  @if ((leadSourceTagsByLeadSource$ | async).length) {
    <div class="mx-3 my-1 flex items-end justify-between">
      <label class="m-0 text-[11px]">
        Available tags
        @if ((leadSourceTagsByLeadSource$ | async).length > 10) {
          <span class="ml-1 inline-flex rounded-xl bg-grey-blue px-1 py-0.5 text-xxs font-medium text-white">
            {{ (leadSourceTagsByLeadSource$ | async).length }}
          </span>
        }
      </label>
      <button
        id="toggle-lead-source-tag-filter"
        type="button"
        class="btn-xs btn-default m-0 flex gap-1 items-center"
        [class.text-blue-bright]="isLeadSourceTagFilterOpen"
        [attr.aria-expanded]="isLeadSourceTagFilterOpen"
        aria-label="Filter available tags"
        tooltip="Filter available tags"
        (click)="toggleLeadSourceTagFilter()"
      >
        <ion-icon name="filter-outline" class="size-3"></ion-icon>
        Filter
      </button>
    </div>
    <div
      class="mx-2.5 rounded bg-white transition-colors border"
      [ngClass]="{
        'overflow-hidden shadow-sm p-2': isLeadSourceTagFilterOpen || filteredLeadSourceTagsThreshold !== 5,
        'border-grey-concrete': filteredLeadSourceTagsThreshold !== 5 && !isLeadSourceTagFilterOpen,
        'border-blue-bright': isLeadSourceTagFilterOpen,
        'border-transparent': filteredLeadSourceTagsThreshold === 5 && !isLeadSourceTagFilterOpen,
      }"
    >
      <div
        class="overflow-hidden opacity-0 transition-all duration-200"
        [class.opacity-100]="isLeadSourceTagFilterOpen"
        [class.h-0]="!isLeadSourceTagFilterOpen"
      >
        <div class="relative w-full">
          <input
            #leadSourceTagFilter
            id="lead-source-tag-filter"
            class="!m-0 !border-0 !bg-transparent !px-2 !py-1 !text-xs !shadow-none !outline-none !ring-0 w-full"
            type="text"
            autocomplete="off"
            autofocus="true"
            maxlength="16"
            placeholder="Filter tags"
            [value]="leadSourceTagFilterTerm"
            (input)="setLeadSourceTagFilterTerm(leadSourceTagFilter.value)"
          />
          @if (leadSourceTagFilterTerm.length > 0) {
            <button
              id="clear-lead-source-tag-filter"
              type="button"
              class="btn-icon !absolute right-0 top-1/2 !m-0 !size-6 !-translate-y-1/2 !border-0 !bg-transparent !p-0 text-grey-mid !shadow-none"
              aria-label="Clear tag filter"
              tooltip="Clear filter"
              (click)="setLeadSourceTagFilterTerm(''); leadSourceTagFilter.value = ''; leadSourceTagFilter.focus()"
            >
              <ion-icon name="close" class="size-3.5"></ion-icon>
            </button>
          }
        </div>
      </div>

      @if ((filteredLeadSourceTagsByLeadSource$ | async).length) {
        <div class="relative overflow-hidden">
          <div #leadSourceTagChipList class="max-h-40 overflow-x-hidden overflow-y-auto">
            <div class="flex flex-wrap justify-start gap-1.5">
              @for (
                sourceTag of filteredLeadSourceTagsByLeadSource$ | async | slice: 0 : filteredLeadSourceTagsThreshold;
                track sourceTag.id
              ) {
                <button
                  class="!m-0 !min-h-0 inline-flex w-fit cursor-pointer items-center gap-1 rounded-full border bg-none py-0.5 pl-1.5 pr-2 text-left text-xs font-medium leading-4 !shadow-none transition focus:ring-0"
                  [class]="
                    sourceTag.attributes.name === selectedLeadSourceTag
                      ? 'border-primary bg-primary/15 text-primary hover:bg-primary/25 focus-visible:bg-primary/25'
                      : 'border-slate-200 bg-grey-light-concrete/75 text-grey-blue hover:border-primary/50 hover:bg-primary/15 focus-visible:border-primary focus-visible:bg-primary/15 focus-visible:text-primary'
                  "
                  [attr.aria-label]="'Select ' + sourceTag.attributes.name"
                  [attr.aria-pressed]="sourceTag.attributes.name === selectedLeadSourceTag"
                  [attr.data-selected]="sourceTag.attributes.name === selectedLeadSourceTag || null"
                  (click)="selectLeadSourceTag(sourceTag.attributes.name)"
                >
                  <ion-icon
                    [name]="
                      sourceTag.attributes.name === selectedLeadSourceTag ? 'checkmark-circle' : 'ellipse-outline'
                    "
                    class="size-3.5 shrink-0"
                    [class.text-grey-blue/50]="sourceTag.attributes.name !== selectedLeadSourceTag"
                  ></ion-icon>
                  <span class="truncate">{{ sourceTag.attributes.name }}</span>
                </button>
              }
            </div>

            @if ((filteredLeadSourceTagsByLeadSource$ | async).length > filteredLeadSourceTagsThreshold) {
              <div class="flex justify-center">
                <button
                  id="show-all-lead-source-tags"
                  class="btn-xs btn-default m-2"
                  tooltip="Show all available tags"
                  (click)="
                    !filteredLeadSourceTagsThreshold
                      ? (filteredLeadSourceTagsThreshold = 5)
                      : (filteredLeadSourceTagsThreshold = undefined)
                  "
                >
                  Show All
                </button>
              </div>
            }
          </div>
        </div>
      } @else {
        <div class="border-t border-slate-200/80 p-3 text-center">
          <p class="m-0 text-xs font-medium text-grey-blue">No matching tags</p>
          <p class="m-0 text-[10px] font-normal text-grey-mid">Try a different filter.</p>
        </div>
      }
    </div>
  } @else {
    <div
      class="m-2 flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-slate-300 bg-slate-50/65 p-3 text-center"
    >
      <ion-icon name="pricetags-outline" class="size-4 text-grey-mid/60"></ion-icon>
      <p class="m-0 text-xs font-medium text-grey-blue">No source tags yet</p>
      <p class="m-0 text-[10px] font-normal text-grey-mid">Add the first tag below.</p>
    </div>
  }

  <div class="form-input m-0 px-2.5 py-2">
    @if ((permissions$ | async)?.includes('lead_source_tags_admin')) {
      <div class="relative">
        <input
          #newLeadSourceTagInput
          id="new-lead-source-tag"
          class="!pl-10 !pr-16"
          type="text"
          autocomplete="off"
          placeholder="Add a new tag"
          [value]="newLeadSourceTag"
          (input)="newLeadSourceTag = newLeadSourceTagInput.value.trim()"
        />
        <ion-icon
          name="add-outline"
          class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-grey-mid"
        ></ion-icon>

        @if (newLeadSourceTag.length > 0) {
          <button
            id="clear-new-lead-source-tag"
            type="button"
            class="btn-icon !absolute top-1/2 !m-0 !size-7 !-translate-y-1/2 !p-0 text-grey-mid"
            [style.right]="newLeadSourceTag.length > 0 ? '3.25rem' : '0.25rem'"
            aria-label="Clear new tag"
            tooltip="Clear new tag"
            (click)="newLeadSourceTag = ''; newLeadSourceTagInput.value = ''; newLeadSourceTagInput.focus()"
          >
            <ion-icon name="close" class="size-4"></ion-icon>
          </button>
          <button
            id="add-new-lead-source-tag"
            type="button"
            class="btn-primary btn-xs !absolute right-1 top-1/2 !m-0 !-translate-y-1/2"
            (click)="createLeadSourceTag()"
          >
            Add
          </button>
        }
      </div>
    }
  </div>
}
